import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { TemplatesProvider } from './context/TemplatesContext';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import Header from './components/layout/Header';
import Sidebar from './components/layout/Sidebar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ToastContainer from './components/common/ToastContainer';

// Lazy load page components for code splitting
const Login = lazy(() => import('./pages/Login'));
const QuotationForm = lazy(() => import('./pages/QuotationForm'));
const QuotationList = lazy(() => import('./pages/QuotationList'));
const QuotationDetail = lazy(() => import('./pages/QuotationDetail'));
const ShippingDocumentPage = lazy(() => import('./pages/ShippingDocumentPage'));
const InvoiceList = lazy(() => import('./pages/InvoiceList'));
const InvoiceDetail = lazy(() => import('./pages/InvoiceDetail'));
const MessageTemplatesSettings = lazy(() => import('./pages/MessageTemplatesSettings'));
const ContractTemplateSettings = lazy(() => import('./pages/ContractTemplateSettings'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CompanyManagement = lazy(() => import('./pages/CompanyManagement'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Customer management pages
const CustomerList = lazy(() => import('./pages/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/CustomerDetail'));
const CustomerForm = lazy(() => import('./pages/CustomerForm'));

// Asset (product) management pages
const AssetList = lazy(() => import('./pages/AssetList'));
const AssetForm = lazy(() => import('./pages/AssetForm'));

// Dashboard Simple page
const DashboardSimple = lazy(() => import('./pages/DashboardSimple'));

// Signature page (public access)
const SignaturePage = lazy(() => import('./pages/SignaturePage'));

// Public customer view page (accessed via LINE link, HMAC token)
const PublicDocumentView = lazy(() => import('./pages/PublicDocumentView'));

// Contract management pages
const ContractList = lazy(() => import('./pages/ContractList'));
const ContractForm = lazy(() => import('./pages/ContractForm'));
const ContractDetail = lazy(() => import('./pages/ContractDetail'));
const ContractSignPage = lazy(() => import('./pages/ContractSignPage'));
const ContractFromTemplate = lazy(() => import('./pages/ContractFromTemplate'));

// Company settings page
const CompanySettings = lazy(() => import('./pages/CompanySettings'));

// Loading component
const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p>載入中...</p>
  </div>
);

// Layout component that conditionally renders Header
const AppLayout = ({ children }) => {
  const location = useLocation();
  const isSignaturePage = location.pathname.startsWith('/signature/');
  const isContractSignPage = location.pathname.startsWith('/contract-sign/');
  const isPublicViewPage = location.pathname.startsWith('/view/');
  const isPublicPage = isSignaturePage || isContractSignPage || isPublicViewPage;
  const isLoginPage = location.pathname === '/login';
  const showSidebar = !isPublicPage && !isLoginPage;

  return (
    <div className={`App${showSidebar ? ' App--with-sidebar' : ''}`}>
      {!isPublicPage && <Header />}
      <div className={showSidebar ? "app-body" : ""}>
        {showSidebar && <Sidebar />}
        <main className={isPublicPage ? "signature-container" : "app-main"}>
          {children}
        </main>
      </div>
      {!isPublicPage && <Footer />}
      <ToastContainer />
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <TemplatesProvider>
          <ToastProvider>
            <AppLayout>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <Login />
                  </Suspense>
                } />
                
                {/* Signature page - Public access with token */}
                <Route path="/signature/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <SignaturePage />
                  </Suspense>
                } />

                {/* Contract sign page - Public access with token */}
                <Route path="/contract-sign/:token" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <ContractSignPage />
                  </Suspense>
                } />

                {/* Public customer document view - accessed via LINE link */}
                <Route path="/view/:docType/:docId" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PublicDocumentView />
                  </Suspense>
                } />
                
                {/* Protected Routes - All authenticated users */}
                <Route element={<ProtectedRoute />}>
                  <Route path="/" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DashboardSimple />
                    </Suspense>
                  } />
                  <Route path="/profile" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserProfile />
                    </Suspense>
                  } />
                  <Route path="/dashboard" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Dashboard />
                    </Suspense>
                  } />
                  <Route path="/quotations" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationList />
                    </Suspense>
                  } />
                  <Route path="/quotations/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationDetail />
                    </Suspense>
                  } />
                  <Route path="/shipping/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ShippingDocumentPage />
                    </Suspense>
                  } />
                  <Route path="/create" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm />
                    </Suspense>
                  } />
                  <Route path="/edit/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <QuotationForm />
                    </Suspense>
                  } />
                  <Route path="/invoices" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InvoiceList />
                    </Suspense>
                  } />
                  <Route path="/invoices/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <InvoiceDetail />
                    </Suspense>
                  } />
                  
                  {/* Customer Management Routes */}
                  <Route path="/customers" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerList />
                    </Suspense>
                  } />
                  <Route path="/customers/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />
                  <Route path="/customers/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerDetail />
                    </Suspense>
                  } />
                  <Route path="/customers/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerForm />
                    </Suspense>
                  } />

                  {/* Asset (product) Management Routes */}
                  <Route path="/assets" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetList />
                    </Suspense>
                  } />
                  <Route path="/assets/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />
                  <Route path="/assets/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <AssetForm />
                    </Suspense>
                  } />

                  {/* Dashboard Simple Route */}
                  <Route path="/dashboard-simple" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <DashboardSimple />
                    </Suspense>
                  } />

                  {/* Contract Management Routes */}
                  <Route path="/contracts" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractList />
                    </Suspense>
                  } />
                  <Route path="/contracts/new" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractForm />
                    </Suspense>
                  } />
                  {/* CARQ-95 第二期：從範本新增合約（新入口，不動舊的 /contracts/new） */}
                  <Route path="/contracts/new-from-template" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractFromTemplate />
                    </Suspense>
                  } />
                  <Route path="/contracts/:id" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractDetail />
                    </Suspense>
                  } />
                  <Route path="/contracts/:id/edit" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractForm />
                    </Suspense>
                  } />
                  {/* CARQ-95：合約範本不分權限（系統只有一位使用者） */}
                  <Route path="/contract-templates" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ContractTemplateSettings />
                    </Suspense>
                  } />
                </Route>
                
                {/* Admin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  <Route path="/message-templates" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <MessageTemplatesSettings />
                    </Suspense>
                  } />
                  <Route path="/company-settings" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanySettings />
                    </Suspense>
                  } />
                </Route>

                {/* Superadmin-only Routes */}
                <Route element={<ProtectedRoute allowedRoles={['superadmin']} />}>
                  <Route path="/companies" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CompanyManagement />
                    </Suspense>
                  } />
                  {false && <Route path="/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />}
                </Route>

                {/* User Management - Accessible by superadmin and admin */}
                {false && <Route element={<ProtectedRoute allowedRoles={['admin', 'superadmin']} />}>
                  <Route path="/companies/:companyId/users" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <UserManagement />
                    </Suspense>
                  } />
                </Route>}
                
                {/* Not Found */}
                <Route path="*" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <NotFound />
                  </Suspense>
                } />
              </Routes>
            </AppLayout>
          </ToastProvider>
        </TemplatesProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
